export interface Pdf417Result {
  decodedResult?: string;
  detectedGuards: number;
  scannedImage?: string;
}

export async function scanForPdf417(
  source: HTMLCanvasElement,
  attemptRotationAngles: number[],
  sourceCoordinates: { x: number; y: number; width: number; height: number } = {
    x: 0,
    y: 0,
    width: source.width,
    height: source.height,
  },
): Promise<Pdf417Result> {
  const canvasContext = source.getContext('2d', { willReadFrequently: true });

  if (canvasContext == null || sourceCoordinates.width <= 0 || sourceCoordinates.height <= 0) {
    return { detectedGuards: 0 };
  }

  // Increase constrast then parse with ZXing
  // Does not use safeGetCanvasImageData since the params are a bit more complex
  const imageData = canvasContext?.getImageData(
    sourceCoordinates.x,
    sourceCoordinates.y,
    sourceCoordinates.width,
    sourceCoordinates.height,
  );

  const ZXing = (window as any).ZXing;
  // Wait until ZXing loads (async)
  if (imageData && ZXing) {
    const luminanceSource = new ZXing.BitmapLuminanceSource(imageData);
    const binarizer = new ZXing.Common.HybridBinarizer(luminanceSource);

    // See if barcode exists
    const bitmapSource = new ZXing.BinaryBitmap(binarizer);
    return scanForPdf417Rotations(source, bitmapSource, attemptRotationAngles);
  } else {
    return { detectedGuards: 0 };
  }
}

interface BinaryBitmap {
  rotateCounterClockwise: () => BinaryBitmap;
}

function scanForPdf417Rotations(
  source: HTMLCanvasElement,
  bitmap: BinaryBitmap,
  attemptRotationAngles: number[],
): Pdf417Result {
  const ZXing = (window as any).ZXing;

  for (const currentRotation of attemptRotationAngles) {
    switch (currentRotation) {
      case 90:
        bitmap = bitmap.rotateCounterClockwise();
        break;

      case 180:
        bitmap = bitmap.rotateCounterClockwise().rotateCounterClockwise();
        break;
    }

    const detectResult = ZXing.PDF417.Internal.Detector.detectSingle(bitmap, null, false);

    // The left guard is displayed in points 0, 1, 4, 5. The right guard is 2, 3, 6, 7
    // If a guard is undetected, the points will be null.
    if (detectResult.Points.length > 0) {
      const decodeResults = ZXing.PDF417.PDF417Reader.decode(bitmap, null, false);
      const decodedResult = decodeResults.length > 0 && decodeResults[0].Text;

      return {
        decodedResult,
        detectedGuards: detectResult.Points[0].filter(Boolean).length / 4,
        scannedImage: source.toDataURL('image/jpeg'),
      };
    }
  }

  return {
    detectedGuards: 0,
  };
}

export interface AamvaBarcode {
  firstName?: string;
  lastName?: string;
  middleName?: string;
  dateOfBirth?: string;
  street1?: string;
  street2?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  issuingCountry?: string;
}

export function parseAamva(barcode: string) {
  const versionMatches = /\d{6}(\d{2})\w+/.exec(barcode);
  const version = versionMatches && versionMatches[1];

  const aamvaKeys = {
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    firstName: ['02', '03'].indexOf(version) !== -1 ? 'DCT' : 'DAC',
    lastName: version === '01' ? 'DAB' : 'DCS',
    middleName: 'DAD',
    dateOfBirth: 'DBB',
    street1: 'DAG',
    street2: 'DAH',
    city: 'DAI',
    state: 'DAJ',
    postalCode: 'DAK',
    issuingCountry: 'DCG',
  };

  const result: AamvaBarcode = {};
  for (const field of Object.keys(aamvaKeys)) {
    const key = aamvaKeys[field as keyof typeof aamvaKeys];
    const keyMatches = new RegExp(`\\s*${key}(.+)(\\n|$)`).exec(barcode);
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    result[field as keyof typeof aamvaKeys] = keyMatches && keyMatches[1].trim();
  }

  return result;
}
